import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { PubSub, PubSubCallback } from '@instinet-analytics/core-lib';
import { v4 as uuid } from 'uuid';
import { addItemToArrayWithMaxSize } from 'utils/arrays/arrayUtils';

export interface MeasureUserAgentSpecificMemoryResult {
    bytes: number;
}

export interface MeasureUserAgentSpecificMemoryTimestampedResult {
    timeStamp: Date;
    result: MeasureUserAgentSpecificMemoryResult;
}

/*
 * Interface for a "cross-origin isolated" site (see https://web.dev/coop-coep/ and https://web.dev/monitor-total-page-memory-usage/)
 */
interface CrossOriginIsolatedEnvironmentPerformanceObject {
    measureUserAgentSpecificMemory?: () => Promise<MeasureUserAgentSpecificMemoryResult>;
}

export class MemoryMonitoringService {
    private logger: Logger;
    private scheduledMemoryMeasurementEventBus: PubSub<MeasureUserAgentSpecificMemoryResult>;
    public results: MeasureUserAgentSpecificMemoryTimestampedResult[] = [];

    public constructor(loggingService: LoggingService) {
        this.logger = buildLogger('MemoryMonitoringService', loggingService);

        this.scheduledMemoryMeasurementEventBus =
            new PubSub<MeasureUserAgentSpecificMemoryResult>();

        if (this.performance.measureUserAgentSpecificMemory) {
            this.logger.debug('Starting scheduled memory measurement');
            setTimeout(this.scheduledMeasure.bind(this), 0);
        } else {
            this.logger.warn(
                'performance.measureUserAgentSpecificMemory() is not available: unable to schedule memory measurement'
            );
        }
    }

    public async measure(): Promise<MeasureUserAgentSpecificMemoryResult> {
        if (!this.performance.measureUserAgentSpecificMemory) {
            throw new Error(
                'performance.measureUserAgentSpecificMemory() is not available.'
            );
        }

        try {
            this.logger.debug('Measuring memory...');

            const memoryResult =
                await this.performance.measureUserAgentSpecificMemory();

            this.results = addItemToArrayWithMaxSize(
                {
                    timeStamp: new Date(),
                    result: memoryResult,
                },
                this.results,
                100
            );
            this.logger.debug('Measured memory', memoryResult);
            return memoryResult;
        } catch (error) {
            this.logger.warn('Error measuring memory usage', error);
            throw error;
        }
    }

    public subscribe(cb: PubSubCallback<MeasureUserAgentSpecificMemoryResult>) {
        this.scheduledMemoryMeasurementEventBus.subscribe(uuid(), cb);
        return this.results[0];
    }

    private get performance(): CrossOriginIsolatedEnvironmentPerformanceObject {
        return performance as CrossOriginIsolatedEnvironmentPerformanceObject;
    }

    private async scheduledMeasure() {
        try {
            const result = await this.measure();
            this.scheduledMemoryMeasurementEventBus.publish(result);
        } catch (err) {
            this.logger.warn('Error measuring memory', err);
        }

        const interval = measurementInterval();
        setTimeout(this.scheduledMeasure.bind(this), interval);
    }
}

function measurementInterval() {
    // Random interval in milliseconds with on average there is one measurement every five minutes.
    const MEAN_INTERVAL_IN_MS = 2 * 60 * 1000;
    return -Math.log(Math.random()) * MEAN_INTERVAL_IN_MS;
}
